import { supabase } from '@/integrations/supabase/client';
import { logger } from './monitoring/logger';

interface AnalyticsEvent {
  event_name: string;
  properties?: Record<string, any>;
  user_id?: string;
  session_id?: string;
}

class Analytics {
  private static instance: Analytics;
  private sessionId: string;
  
  private constructor() {
    // Generate a unique session ID
    this.sessionId = this.generateSessionId();
  }
  
  static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  async track(event_name: string, properties?: Record<string, any>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Log to console in development
      if (import.meta.env.DEV) {
        console.log('[Analytics]', event_name, properties);
      }

      const eventData = {
        user_id: user?.id,
        event_name,
        properties: properties || {},
        user_agent: navigator.userAgent,
        url: window.location.href,
        session_id: this.sessionId,
      };

      // Store in database
      const { error } = await supabase
        .from('analytics_events')
        .insert(eventData);

      if (error) {
        logger.error('[Analytics] Failed to store event', error);
      }
    } catch (error) {
      logger.error('[Analytics] Error tracking event', error);
    }
  }

  pageView(page: string) {
    this.track('page_view', { page });
  }

  streamView(streamId: string, streamTitle: string) {
    this.track('stream_view', { stream_id: streamId, stream_title: streamTitle });
  }

  tipSent(amount: number, streamId: string) {
    this.track('tip_sent', { amount, stream_id: streamId });
  }

  pollVote(pollId: string, optionIndex: number) {
    this.track('poll_vote', { poll_id: pollId, option_index: optionIndex });
  }

  walletTopUp(amount: number, coins: number) {
    this.track('wallet_topup', { amount, coins });
  }

  streamCreated(streamId: string, category: string) {
    this.track('stream_created', { stream_id: streamId, category });
  }

  // Engagement tracking
  userEngagement(action: string, details?: Record<string, any>) {
    this.track('user_engagement', { action, ...details });
  }

  exitIntent() {
    this.track('exit_intent_triggered', { page: window.location.pathname });
  }

  onboardingStep(step: number, completed: boolean) {
    this.track('onboarding_step', { step, completed });
  }

  ctaClick(ctaName: string, location: string) {
    this.track('cta_click', { cta_name: ctaName, location });
  }

  timeOnPage(duration: number) {
    this.track('time_on_page', { 
      duration_seconds: Math.round(duration / 1000),
      page: window.location.pathname 
    });
  }
}

export const analytics = Analytics.getInstance();
